import { inject, Injectable } from '@angular/core';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap, delay, filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { ZefAuthApi } from './auth.api';
import {
  zefLoginSuccess,
  zefLoginFail,
  zefLogin,
  zefCheckStoredToken,
  zefCheckStoredTokenSuccess,
  zefCheckStoredTokenFail,
  zefLogout,
  zefApiLogout,
  zefApiLogoutSuccess,
  zefApiLogoutFail,
  zefSetToken
} from './auth.action';
import { AuthTokenStorageService } from './auth.utils';
import { ROUTES_WITHOUT_TOKEN_CHECK } from './auth.constant';
import { ZefAuthState } from './auth.model';
import { zefSelectAuthState } from './auth.selector';
import { zefResetState } from '../core';

@Injectable()
export class AuthEffect {

  #actions$ = inject(Actions);
  #routesWithoutTokenCheck = inject(ROUTES_WITHOUT_TOKEN_CHECK);
  #api = inject(ZefAuthApi);
  #tokenStorage = inject(AuthTokenStorageService);
  #store = inject(Store);

  private _initType = '[@zerops/zef/auth] effect-init';

  onLogin$ = createEffect(() => this.#actions$.pipe(
    ofType(zefLogin),
    switchMap((action) => this.#api
      .login$({
        ...action.data
      })
      .pipe(
        map((response) => zefLoginSuccess(
          response.tokenData,
          action,
          response.response
        )),
        catchError((err) => of(zefLoginFail(err, action)))
      )
    )
  ));

  onLoginSuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(zefLoginSuccess),
    withLatestFrom(this.#store.select(zefSelectAuthState)),
    mergeMap(([ { data }, authState ]) => [
      /**
       * Repeated login while another user is already authorized
       * (e.g. /login opened in another tab),
       * clear data of the previous session before setting the new token.
       */
      ...(authState === ZefAuthState.Authorized ? [ zefResetState() ] : []),
      zefSetToken(data, zefLoginSuccess.type)
    ])
  ));

  onSetToken$ = createEffect(() => this.#actions$.pipe(
    ofType(zefSetToken),
    tap(({ data }) => this.#tokenStorage.setToken(data))
  ), { dispatch: false });

  onInitTriggerTokenCheck$ = createEffect(() => this.#actions$.pipe(
    ofType(this._initType),
    filter(() => {
      const currentRoute = window.location.pathname.substring(window.location.pathname.startsWith('/') ? 1 : 0);
      const skipTokenCheck = this.#routesWithoutTokenCheck.some(
        (route) => currentRoute.match(new RegExp(`^${route}(/|$)`))
      );
      return !skipTokenCheck;
    }),
    map(() => zefCheckStoredToken(this.#tokenStorage.isValid()))
  ));

  onCheckToken$ = createEffect(() => this.#actions$.pipe(
    ofType(zefCheckStoredToken),
    map(({ valid }) => {
      if (valid) {
        return zefCheckStoredTokenSuccess(this.#tokenStorage.getToken());
      } else {
        return zefCheckStoredTokenFail();
      }
    })
  ));

  onLogout$ = createEffect(() => this.#actions$.pipe(
    ofType(zefLogout),
    map(() => zefApiLogout())
  ));

  onApiLogoutRemoveStoredToken$ = createEffect(() => this.#actions$.pipe(
    ofType(zefApiLogout),
    // delay needed to let api trigger with auth header
    delay(50),
    tap(() => this.#tokenStorage.removeToken())
  ), { dispatch: false });

  onApiLogout$ = createEffect(() => this.#actions$.pipe(
    ofType(zefApiLogout),
    switchMap(() => this.#api
      .logout$()
      .pipe(
        map(() => zefApiLogoutSuccess()),
        catchError(() => of(zefApiLogoutFail()))
      )
    )
  ));

  ngrxOnInitEffects() {
    return { type: this._initType };
  }

}
